import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { NbButtonModule, NbCardModule, NbDialogRef, NbSpinnerModule } from '@nebular/theme';
import { finalize } from 'rxjs/operators';
import { FrequenciaService } from '../../../core/api/frequencia.service';

import { mensagemDeErro } from '../../../utils/mensagem-erro';
/**
 * Popup com os dias em que o catequizando faltou na turma.
 * Fonte: `GET /frequencia/{turmaId}/catequizandos/{pessoaId}/faltas`.
 */
@Component({
  selector: 'app-faltas-datas-dialog',
  standalone: true,
  imports: [CommonModule, NbCardModule, NbButtonModule, NbSpinnerModule],
  template: `
    <nb-card class="dialog-alert faltas-datas-dialog">
      <nb-card-header>
        <span class="titulo">Faltas de {{ pessoaNome || 'catequizando' }}</span>
        <span class="subtitulo" *ngIf="turmaNome">{{ turmaNome }}</span>
      </nb-card-header>

      <nb-card-body>
        <div class="carregando" *ngIf="carregando">
          <nb-spinner status="primary" size="medium"></nb-spinner>
          <span>Carregando datas…</span>
        </div>

        <p class="erro" *ngIf="erro">{{ erro }}</p>

        <ng-container *ngIf="!carregando && !erro">
          <p class="resumo">
            <strong>{{ datas.length }}</strong>
            {{ datas.length === 1 ? 'falta registrada' : 'faltas registradas' }}
          </p>
          <ul class="lista-datas" *ngIf="datas.length">
            <li *ngFor="let d of datas">{{ d | date: 'dd/MM/yyyy' }}</li>
          </ul>
          <p class="vazio" *ngIf="!datas.length">Nenhuma falta registrada nesta turma.</p>
        </ng-container>
      </nb-card-body>

      <nb-card-footer class="footer-actions">
        <button nbButton status="primary" size="medium" type="button" (click)="fechar()">Fechar</button>
      </nb-card-footer>
    </nb-card>
  `,
  styles: [
    `
      .faltas-datas-dialog nb-card-header {
        display: flex;
        flex-direction: column;
        gap: 0.2rem;
      }

      .titulo {
        font-weight: 600;
        line-height: 1.3;
        word-break: break-word;
      }

      .subtitulo {
        font-size: 0.82rem;
        font-weight: 400;
        color: var(--text-hint-color, #8f9bb3);
        line-height: 1.35;
        word-break: break-word;
      }

      .carregando {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        color: var(--text-hint-color, #8f9bb3);
      }

      .erro {
        margin: 0;
        color: #db2c66;
      }

      .resumo {
        margin: 0 0 0.75rem;
        color: var(--text-hint-color, #8f9bb3);
      }

      .lista-datas {
        margin: 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }

      .lista-datas li {
        padding: 0.35rem 0.7rem;
        border-radius: 999px;
        background: #fee2e2;
        color: #9f1239;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
      }

      .vazio {
        margin: 0;
        color: var(--text-hint-color, #8f9bb3);
      }
    `,
  ],
})
export class FaltasDatasDialogComponent implements OnInit {
  @Input() turmaId!: string;
  @Input() pessoaId!: string;
  @Input() pessoaNome = '';
  @Input() turmaNome = '';

  datas: Date[] = [];
  carregando = false;
  erro: string | null = null;

  private readonly frequenciaService = inject(FrequenciaService);
  private readonly ref = inject(NbDialogRef<FaltasDatasDialogComponent>);

  ngOnInit(): void {
    if (!this.turmaId || !this.pessoaId) {
      this.erro = 'Catequizando sem identificação para consultar as faltas.';
      return;
    }
    this.carregando = true;
    this.frequenciaService
      .listarFaltasDoCatequizando(this.turmaId, this.pessoaId)
      .pipe(finalize(() => (this.carregando = false)))
      .subscribe({
        next: (res) => {
          this.pessoaNome = res?.pessoaNome || this.pessoaNome;
          this.turmaNome = res?.turmaNome || this.turmaNome;
          this.datas = (res?.datasFaltas ?? [])
            .map((iso) => this.paraData(iso))
            .filter((d): d is Date => d !== null)
            .sort((a, b) => b.getTime() - a.getTime());
        },
        error: (err) => {
          this.erro =
            mensagemDeErro(err, 'Não foi possível carregar as datas das faltas.');
        },
      });
  }

  /** `yyyy-MM-dd` → Date local (evita o deslocamento de fuso do construtor com string ISO). */
  private paraData(iso: string): Date | null {
    if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  fechar(): void {
    this.ref.close();
  }
}
